"use client"

import React, { useContext, useState, useEffect } from "react"
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Avatar,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Divider,
  Paper,
  IconButton,
  InputAdornment,
  Chip,
  alpha,
  useTheme,
  Alert,
  Fade,
  Skeleton,
  Stack,
  Switch,
  Tooltip,
} from "@mui/material"
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Link as LinkIcon,
  Description as DescriptionIcon,
  CalendarMonth as CalendarIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  NewReleases as NewsIcon,
  LocalOffer as PromotionIcon,
  ToggleOn as ActiveIcon,
  ToggleOff as InactiveIcon,
} from "@mui/icons-material"
import axios from "axios"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AppContext } from "../../contexts/AppContext"

function PromotionEditor({ idPartner, idPromotion, onCancel, onSave }) {
  const { state } = useContext(AppContext)
  const queryClient = useQueryClient()
  const theme = useTheme()

  const { data, isLoading, error } = useQuery({
    queryKey: ["promotion", idPromotion],
    queryFn: async () => {
      if (!idPromotion) return null
      const response = await axios.get(`/api/v2/partner/${idPartner}/announcements/${idPromotion}`, {
        headers: { Authorization: state.token ? `Bearer ${state.token}` : "" },
      })
      return response.data
    },
    enabled: !!idPromotion,
  })

  const [label, setLabel] = useState("promotion")
  const [url, setUrl] = useState(null)
  const [text, setText] = useState("")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [validationError, setValidationError] = useState(null)
  const [media, setMedia] = useState(null)
  const [deleteImage, setDeleteImage] = useState(false)
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    if (data) {
      setLabel(data.label || "promotion")
      setUrl(data.url || null)
      setText(data.text || "")
      setStartDate(data?.start_date ? data.start_date.split("T")[0] : "")
      setEndDate(data?.start_date ? data.end_date.split("T")[0] : "")
      setPreview(data.image || "")
      setIsActive(data.isActive !== undefined ? data.isActive : true)
      if (data.hasMedia && data.hasMedia.length > 0) {
        setMedia(data.hasMedia[0].media_id)
      }
      setDeleteImage(false)
    }
  }, [data])

  const mutation = useMutation({
    mutationFn: async ({ formData }) => {
      const url = idPromotion
        ? `/api/v2/partner/${idPartner}/announcements/${idPromotion}`
        : `/api/v2/partner/${idPartner}/announcements/`
      const method = idPromotion ? "put" : "post"

      const response = await axios({
        method,
        url,
        data: formData,
        headers: {
          Authorization: state.token ? `Bearer ${state.token}` : "",
          "Content-Type": "multipart/form-data",
        },
      })

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["promotion"])
      if (onSave) onSave()
    },
    onError: (error) => {
      console.error("Error saving promotion:", error)
      setValidationError("Failed to save. Please try again.")
    },
  })

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile && selectedFile.type.startsWith("image/")) {
      setFile(selectedFile)
      setPreview(URL.createObjectURL(selectedFile))
      setDeleteImage(false)
    }
  }

  const handleSave = () => {
    if (!startDate || !endDate || endDate < startDate) {
      setValidationError("Please check your dates. End date must be after start date.")
      return
    }

    const formData = new FormData()
    formData.append("label", label)
    if (url !== null) {
      formData.append("url", url)
    }
    formData.append("text", text)
    formData.append("start_date", startDate)
    formData.append("end_date", endDate)
    formData.append("isActive", isActive)

    // Handle image upload or deletion
    if (file) {
      formData.append("media", file)
    } else if (deleteImage) {
      formData.append("delete_media", "true")
    }

    mutation.mutate({ formData })
  }

  // Custom file input reference
  const fileInputRef = React.useRef(null)
  const handleUploadClick = () => {
    fileInputRef.current.click()
  }

  if (isLoading) {
    return (
      <Card elevation={3} sx={{ maxWidth: 700, mx: "auto", mt: 2, borderRadius: 2 }}>
        <CardHeader
          title={<Skeleton width="60%" />}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        />
        <CardContent>
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={56} />
            <Skeleton variant="rectangular" height={56} />
            <Skeleton variant="rectangular" height={56} />
            <Box display="flex" gap={2}>
              <Skeleton variant="rectangular" height={56} width="50%" />
              <Skeleton variant="rectangular" height={56} width="50%" />
            </Box>
            <Skeleton variant="rectangular" height={100} />
          </Stack>
        </CardContent>
        <CardActions sx={{ justifyContent: "flex-end", p: 2 }}>
          <Skeleton variant="rectangular" width={100} height={36} sx={{ mr: 1 }} />
          <Skeleton variant="rectangular" width={100} height={36} />
        </CardActions>
      </Card>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ maxWidth: 600, mx: "auto", mt: 2 }}>
        Error loading promotion data. Please try again later.
      </Alert>
    )
  }

  const hasImage = !deleteImage && (preview || media)

  return (
    <Fade in={true} timeout={300}>
      <Card elevation={3} sx={{ maxWidth: 700, mx: "auto", borderRadius: 2, overflow: "visible" }}>
        <CardContent sx={{ p: 1.5 }}>
          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setValidationError(null)}>
              {validationError}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Promotion Type</InputLabel>
                <Select
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  label="Promotion Type"
                  startAdornment={
                    <InputAdornment position="start" sx={{ ml: 1, mr: -0.5 }}>
                      {label === "news" ? <NewsIcon color="info" /> : <PromotionIcon color="secondary" />}
                    </InputAdornment>
                  }
                >
                  <MenuItem value="news">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <NewsIcon sx={{ mr: 1 }} color="info" />
                      News
                    </Box>
                  </MenuItem>
                  <MenuItem value="promotion">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <PromotionIcon sx={{ mr: 1 }} color="secondary" />
                      Promotion
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <Paper
                elevation={0}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1,
                  borderRadius: 2,
                  bgcolor: isActive ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                  border: `1px solid ${
                    isActive ? alpha(theme.palette.success.main, 0.3) : alpha(theme.palette.error.main, 0.3)
                  }`,
                  minWidth: 120,
                  transition: "all 0.3s ease",
                }}
              >
                <Tooltip
                  title={
                    isActive
                      ? "This promotion is active and visible to users"
                      : "This promotion is inactive and hidden from users"
                  }
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Switch
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      color={isActive ? "success" : "error"}
                    />
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{ color: isActive ? "success.main" : "error.main" }}
                      >
                        {isActive ? "Active" : "Inactive"}
                      </Typography>
                    </Box>
                  </Box>
                </Tooltip>
              </Paper>
            </Box>

            <TextField
              fullWidth
              label="URL (Optional)"
              value={url || ""}
              onChange={(e) => setUrl(e.target.value)}
              variant="outlined"
              placeholder="https://example.com"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Description"
              value={text}
              onChange={(e) => setText(e.target.value)}
              variant="outlined"
              multiline
              rows={3}
              placeholder="Enter a detailed description of your promotion"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}>
                    <DescriptionIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                error={validationError && !startDate}
              />
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                error={validationError && (!endDate || endDate < startDate)}
              />
            </Box>

            <Divider sx={{ my: .2 }}>
              <Chip
                label={hasImage ? "Promotion Image" : "Add Promotion Image"}
                variant="outlined"
                icon={<ImageIcon />}
              />
            </Divider>

            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
              {hasImage ? (
                <Paper
                  elevation={2}
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: 200,
                    overflow: "hidden",
                    borderRadius: 2,
                  }}
                >
                  <Avatar
                    src={preview || `/api/media/${media}`}
                    variant="square"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 1 }}>
                    <IconButton
                      size="small"
                      sx={{
                        bgcolor: "rgba(0,0,0,0.5)",
                        color: "white",
                        "&:hover": { bgcolor: "rgba(25,118,210,0.8)" },
                      }}
                      onClick={handleUploadClick}
                      title="Replace image"
                    >
                      <UploadIcon />
                    </IconButton>
                  </Box>
                </Paper>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    width: "100%",
                    height: 200,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed",
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    cursor: "pointer",
                  }}
                  onClick={handleUploadClick}
                >
                  <UploadIcon sx={{ fontSize: 40, color: alpha(theme.palette.primary.main, 0.5), mb: 1 }} />
                  <Typography variant="body1" color="textSecondary">
                    Click to upload an image
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    JPG, PNG or GIF
                  </Typography>
                </Paper>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />

              {file && (
                <Typography variant="caption" color="textSecondary">
                  Selected: {file.name}
                </Typography>
              )}

              {deleteImage && (
                <Alert severity="info" sx={{ width: "100%" }}>
                  The image will be removed when you save the promotion.
                </Alert>
              )}

              {!hasImage && !file && (
                <Button variant="outlined" startIcon={<UploadIcon />} onClick={handleUploadClick} sx={{ mt: 1 }}>
                  Select Image
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>

        <CardActions
          sx={{ justifyContent: "space-between", p: 3, bgcolor: alpha(theme.palette.background.default, 0.5) }}
        >
          <Button
            variant="outlined"
            color="error"
            onClick={onCancel}
            startIcon={<CancelIcon />}
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<SaveIcon />}
            disabled={mutation.isPending}
            sx={{ borderRadius: 1.5 }}
          >
            {mutation.isPending ? "Saving..." : "Save Promotion"}
          </Button>
        </CardActions>
      </Card>
    </Fade>
  )
}

export default PromotionEditor

